<template>
    <div>
        <template v-if="loading">
            Loading <i class="fa fa-spinner fa-spin"></i>
        </template>
        <template v-else>
            <vue-headful :title="'[Sync Product Levels] ' + product.product_name + ' - AMOS365'" />

            <nav aria-label="breadcrumb">
                <ol class="breadcrumb">
                    <li class="breadcrumb-item">
                        <router-link to="/products">Products</router-link>
                    </li>
                    <li class="breadcrumb-item">
                        <router-link :to="`/products/view/${product.id}`">{{ product.product_name }}</router-link>
                    </li>
                    <li class="breadcrumb-item active" aria-current="page">Sync Product Levels</li>
                </ol>
            </nav>

            <h4 class="page-header mb-4">
                <span class="text-muted">{{ product.product_name }}</span>: Sync Product Levels
            </h4>

            <div class="row mb-3">
                <div class="col-md-8 col-12">
                    <div class="alert alert-warning">
                        The product level sync will attempt to match one of the product levels values to the legacy plain text commission value based on:
                        <ul>
                            <li>The Product Level Name = Commission Value</li>
                            <!-- <li>Or the Product Level Commission = Commission Value (All "%" will be stripped out)</li> -->
                            <li>Or any manual entered values below.</li>
                        </ul>
                    </div>

                    <div class="alert alert-danger mt-2" v-if="levels.length === 0">
                        This product does not have any levels yet. <router-link :to="`/products/edit/${product.id}`">Add Levels</router-link> before Syncing.
                    </div>

                    <div class="card mb-4" v-for="(level, i) in levels">
                        <div class="card-header">
                            <strong>{{ level.product_level_order }}. {{ level.product_level_name }}</strong>
                        </div>
                        <div class="card-body">
                            <div class="row mb-1">
                                <div class="col-md-4 text-md-right">Product Level Name:</div>
                                <div class="col-md-8">{{ level.product_level_name }}</div>
                            </div>
                            <div class="row mb-1">
                                <div class="col-md-4 text-md-right">Product Level Commission:</div>
                                <div class="col-md-8">{{ level.commission }}</div>
                            </div>
                            <div class="row mb-1">
                                <div class="col-md-4 text-md-right">Additional Sync Values:</div>
                                <div class="col-md-6">
                                    <div v-if="level.sync_values">
                                        <div v-for="(sync, j) in level.sync_values" class="mb-1">
                                            <b-input-group size="sm">
                                                <b-form-input v-model="levels[i].sync_values[j]" placeholder="Enter Sync Value" />
                                                <b-button size="sm" variant="default" v-on:click="removeSyncValue(i, j)" title="Remove Sync Value">
                                                    <i class="fa fa-trash text-danger"></i>
                                                </b-button>
                                            </b-input-group>
                                        </div>
                                    </div>

                                    <div class="cursor-hover" style="border: 1px dashed #ccc; padding: 3px 5px; text-align: center; font-size: 12px;" v-on:click="addSyncValue(i, level)">
                                        <i class="fa fa-plus-circle text-success"></i> Add Sync Value
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-md-4 col-12 mt-md-0 mt-4">
                    <div class="card">
                        <div class="card-header">Analytics: <strong>{{ product.product_name }}</strong></div>
                        <div class="card-body">
                            <div class="mb-2">
                                <span class="text-muted">Contracts Without Level:</span> <strong>{{ contractsWithoutLevel }}</strong>
                            </div>
                            <div>
                                <span class="text-muted">Distinct Commission Values ({{ distinctCommissions.length }}):</span>
                                <ul class="list-unstyled">
                                    <li v-for="commission in distinctCommissions">
                                        {{ commission }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <button type="button" class="btn btn-sm btn-warning" v-on:click="syncLevels()" :disabled="saving || levels.length === 0">
                <template v-if="saving"><i class="fas fa-spin fa-spinner"></i> Syncing...</template>
                <template v-else><i class="fas fa-sync"></i> Sync</template>
            </button>
        </template>
    </div>
</template>

<script>
    export default {
        data() {
            return {
                loading: true,
                saving: false,
                id: this.$route.params.id,
                product: {},
                errors: {},
                levels: [],
                contractsWithoutLevel: null,
                distinctCommissions: [],
            }
        },

        computed: {
            canEdit(){
                //Send it
                return this.$root.hasPermission('products.manage');
            }
        },

        methods: {

            getProduct(){
                //Send it
                this.$http.get('/products/sync-levels/' + this.id)
                    .then(response => {
                        //Set the product
                        this.product = response.data.product;
                        this.levels = response.data.product.levels;
                        this.contractsWithoutLevel = response.data.contractsWithoutLevel;
                        this.distinctCommissions = response.data.distinctCommissions;
                    })
                    .catch(error => {
                        console.log(error);
                    })
                    .then(() => {
                        //Reset
                        this.loading = false;
                    });
            },

            syncLevels(){
                //Helpers
                this.saving = true;
                this.errors = {};

                //Send it
                this.$http
                .post('products/sync-levels/' + this.id, {levels: this.levels})
                .then(response => {
                    //Alert the user?
                    this.$toastr.s('Product Levels Synced');

                    //Return to main list?
                    this.$router.push('/products/view/' + this.id);
                })
                .catch(error => {
                    //Check for error
                    if(error.response.status === 422)
                    {
                        //Display validation errors
                        this.errors = error.response.data.errors;

                        //Alert
                        this.$toastr.e('Validation Errors Occurred');
                    }
                    else
                    {
                        //Alert the user
                        this.$toastr.e(error.response.data, 'Failed to Sync Levels');
                    }
                })
                .then(response => {
                    //SHow loading
                    this.saving = false;
                });
            },

            addSyncValue(i, level){
                //Setup
                if(!this.levels[i].sync_values)
                {
                    this.levels[i].sync_values = [];
                }

                //Push
                this.levels[i].sync_values.push('');

                //Force Update?
                this.$forceUpdate();
            },

            removeSyncValue(i, j){
                //Remove it
                this.levels[i].sync_values.splice(j, 1);

                //Force Update?
                this.$forceUpdate();
            },
        },

        mounted() {
            //get the product?
            this.getProduct();
        }
    }
</script>
